import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ScanSearch, Check, X, Pencil, ExternalLink, Loader2 } from "lucide-react";
import { listPromoProposals, analyzePromos, approvePromo, rejectPromo, updatePromoProposal, euro, mediaUrl } from "@/lib/store";

const STATUS_META = {
  NUOVA: "bg-blue-500/15 text-blue-400", ANALIZZATA: "bg-blue-500/15 text-blue-400",
  PROPOSTA: "bg-indigo-500/15 text-indigo-300", DA_VERIFICARE: "bg-amber-500/15 text-amber-400",
  APPROVATA: "bg-emerald-500/15 text-emerald-400", ATTIVA: "bg-emerald-500/15 text-emerald-400",
  RIFIUTATA: "bg-rose-500/15 text-rose-400", SCADUTA: "bg-zinc-500/15 text-zinc-400",
};

export default function CopilotPromos() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState(null);
  const load = () => listPromoProposals().then(setRows).catch(() => {});
  useEffect(() => { load(); }, []);

  const analyze = async () => {
    setBusy(true);
    try { const r = await analyzePromos(); toast.success(`${r.created} proposte generate dalle locandine`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Analisi non riuscita"); }
    finally { setBusy(false); }
  };

  const approve = async (r) => {
    try { const res = await approvePromo(r.id, { promo_start: r.promo_start || null, promo_end: r.promo_end || null });
      toast.success(`Promo attivata: ${res.prodotto} → ${res.promo}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Approvazione non riuscita"); }
  };
  const reject = async (id) => { await rejectPromo(id); toast.success("Proposta rifiutata"); load(); };

  return (
    <div className="space-y-4" data-testid="copilot-promos">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-mono text-xs uppercase tracking-widest text-zinc-500">Copilot · Promozioni · Analizza locandine</h3>
          <p className="mt-1 text-sm text-zinc-400">Copilot legge le locandine del carosello, riconosce i prodotti e propone i prezzi promo. Nessuna modifica senza la tua approvazione.</p>
        </div>
        <button data-testid="promo-analyze-btn" onClick={analyze} disabled={busy}
          className="flex items-center gap-2 rounded-full bg-[#2563EB] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ScanSearch className="h-4 w-4" />} Analizza locandine
        </button>
      </div>

      {rows.length === 0 && <p className="py-8 text-center text-sm text-zinc-500">Nessuna proposta. Aggiungi locandine nel tab Social e premi “Analizza locandine”.</p>}

      <div className="space-y-3">
        {rows.map((r) => (
          <div key={r.id} data-testid={`promo-proposal-${r.id}`} className="rounded-xl border border-white/10 bg-[#11141A] p-3">
            <div className="flex gap-3">
              <div className="h-24 w-20 shrink-0 overflow-hidden rounded-lg bg-[#0B0E13]">
                {r.offer_image ? <img src={mediaUrl(r.offer_image)} alt="" className="h-full w-full object-cover" /> :
                  <div className="flex h-full items-center justify-center text-[10px] text-zinc-600">no img</div>}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="truncate font-semibold text-white">{r.product_name || r.extracted?.model || "Prodotto non riconosciuto"}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] ${STATUS_META[r.status] || "bg-zinc-500/15 text-zinc-400"}`}>{r.status}</span>
                  {r.confidence != null && <span className="text-[11px] text-zinc-500">match {Math.round(r.confidence * 100)}%</span>}
                </div>
                {r.variant_label && <p className="text-xs text-zinc-400">Variante: {r.variant_label}</p>}
                <div className="mt-1 flex flex-wrap items-center gap-3 text-sm">
                  <span className="text-zinc-400">Attuale: <b className="text-zinc-200">{r.current_price_cents != null ? euro(r.current_price_cents) : "—"}</b></span>
                  <span className="text-zinc-400">Rilevato: <b className="text-emerald-400">{r.proposed_price_cents != null ? euro(r.proposed_price_cents) : "—"}</b></span>
                  {r.extracted?.period && <span className="text-[11px] text-zinc-500">Periodo: {r.extracted.period}</span>}
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {r.status !== "ATTIVA" && r.status !== "RIFIUTATA" && (
                    <button data-testid={`promo-approve-${r.id}`} onClick={() => approve(r)}
                      className="flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white"><Check className="h-3.5 w-3.5" /> Approva</button>
                  )}
                  {r.status !== "RIFIUTATA" && (
                    <button data-testid={`promo-reject-${r.id}`} onClick={() => reject(r.id)}
                      className="flex items-center gap-1 rounded-lg border border-white/10 px-3 py-1.5 text-xs text-rose-400"><X className="h-3.5 w-3.5" /> Rifiuta</button>
                  )}
                  <button data-testid={`promo-modify-${r.id}`} onClick={() => setEdit(r)}
                    className="flex items-center gap-1 rounded-lg border border-white/10 px-3 py-1.5 text-xs text-zinc-200"><Pencil className="h-3.5 w-3.5" /> Modifica</button>
                  {r.product_slug && (
                    <a href={`/prodotto/${r.product_slug}`} target="_blank" rel="noreferrer"
                      className="flex items-center gap-1 rounded-lg border border-white/10 px-3 py-1.5 text-xs text-[#2563EB]"><ExternalLink className="h-3.5 w-3.5" /> Apri prodotto</a>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {edit && <EditModal row={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}

function EditModal({ row, onClose, onSaved }) {
  const [slug, setSlug] = useState(row.product_slug || "");
  const [price, setPrice] = useState(row.proposed_price_cents != null ? (row.proposed_price_cents / 100).toString() : "");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await updatePromoProposal(row.id, { product_slug: slug || null,
        proposed_price_cents: price ? Math.round(parseFloat(price) * 100) : null });
      toast.success("Proposta aggiornata"); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Aggiornamento non riuscito"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-2xl border border-white/10 bg-[#11141A] p-5 text-white" data-testid="promo-edit-modal">
        <h3 className="mb-3 text-lg font-bold">Modifica abbinamento</h3>
        <label className="mb-1 block text-xs text-zinc-400">Slug prodotto (catalogo)</label>
        <input data-testid="promo-edit-slug" value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="es. apple-iphone-17-pro"
          className="mb-3 w-full rounded-xl border border-white/10 bg-[#0B0E13] px-3 py-2.5 text-sm outline-none" />
        <label className="mb-1 block text-xs text-zinc-400">Prezzo promo (€)</label>
        <input data-testid="promo-edit-price" type="number" value={price} onChange={(e) => setPrice(e.target.value)}
          className="mb-4 w-full rounded-xl border border-white/10 bg-[#0B0E13] px-3 py-2.5 text-sm outline-none" />
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="rounded-xl border border-white/10 px-4 py-2 text-sm text-zinc-300">Annulla</button>
          <button data-testid="promo-edit-save" onClick={save} disabled={busy}
            className="flex items-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold disabled:opacity-50">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Salva
          </button>
        </div>
      </div>
    </div>
  );
}
